import type {
  ViewportBehaviorConfig,
  ViewportTargetConfig,
} from '@/config/types/modules/behavior';

import type {
  NormalizedViewportConfig,
  NormalizedViewportTarget,
  ViewportConfigWarning,
} from '../contracts/viewport';

const DEFAULT_ENTER_THRESHOLD = 0.5;
const DEFAULT_EXIT_THRESHOLD = 0;
const DEFAULT_MIN_VISIBLE_MS = 0;
const DEFAULT_TRACK_EXIT = true;
const DEFAULT_ONCE = false;
const DEFAULT_INCLUDE_ELEMENT = false;
const DEFAULT_MUTATION_DEBOUNCE_MS = 100;
const DEFAULT_MAX_OBSERVED_ELEMENTS = 500;
const DEFAULT_ROOT_MARGIN = '0px';

/** Clamp genérico usado pelo normalizador e pelo observer factory. */
export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Normaliza `ViewportBehaviorConfig` (contrato de config) em `NormalizedViewportConfig`
 * (contrato de runtime), coletando warnings sanitizados sem lançar erro global.
 *
 * Selector inválido não é validado aqui — é isolado em runtime na hora do
 * `querySelectorAll` (BEHAVIOR_VIEWPORT_INVALID_SELECTOR).
 */
export function normalizeViewportConfig(
  config: ViewportBehaviorConfig
): NormalizedViewportConfig {
  const warnings: ViewportConfigWarning[] = [];
  const seenKeys = new Set<string>();
  const targets: NormalizedViewportTarget[] = [];

  const rawTargets = Array.isArray(config.targets) ? config.targets : [];

  for (const rawTarget of rawTargets) {
    const normalizedTarget = normalizeTarget(rawTarget, seenKeys, warnings);
    if (normalizedTarget) {
      targets.push(normalizedTarget);
    }
  }

  const mutationDebounceMs = Math.max(
    0,
    toFiniteNumber(config.mutationDebounceMs, DEFAULT_MUTATION_DEBOUNCE_MS)
  );

  const maxObservedElements = Math.max(
    1,
    toFiniteNumber(config.maxObservedElements, DEFAULT_MAX_OBSERVED_ELEMENTS)
  );

  return {
    enabled: config.enabled === true,
    targets,
    rootMargin: isNonEmptyString(config.rootMargin)
      ? config.rootMargin
      : DEFAULT_ROOT_MARGIN,
    observeMutations: config.observeMutations !== false,
    mutationDebounceMs,
    maxObservedElements,
    warnings,
  };
}

function normalizeTarget(
  raw: ViewportTargetConfig,
  seenKeys: Set<string>,
  warnings: ViewportConfigWarning[]
): NormalizedViewportTarget | null {
  const key = typeof raw?.key === 'string' ? raw.key.trim() : '';

  if (!key) {
    warnings.push({
      code: 'BEHAVIOR_VIEWPORT_EMPTY_KEY',
      message: 'Viewport target ignored: empty key.',
    });
    return null;
  }

  if (seenKeys.has(key)) {
    warnings.push({
      code: 'BEHAVIOR_VIEWPORT_DUPLICATE_KEY',
      message: `Viewport target ignored: duplicate key "${key}".`,
    });
    return null;
  }

  const selector = typeof raw?.selector === 'string' ? raw.selector.trim() : '';

  if (!selector) {
    warnings.push({
      code: 'BEHAVIOR_VIEWPORT_EMPTY_SELECTOR',
      message: `Viewport target "${key}" ignored: empty selector.`,
    });
    return null;
  }

  seenKeys.add(key);

  const enterThreshold = clamp(
    toFiniteNumber(raw.enterThreshold, DEFAULT_ENTER_THRESHOLD),
    0,
    1
  );

  const exitThreshold = clamp(
    toFiniteNumber(raw.exitThreshold, DEFAULT_EXIT_THRESHOLD),
    0,
    enterThreshold
  );

  const minVisibleMs = Math.max(
    0,
    toFiniteNumber(raw.minVisibleMs, DEFAULT_MIN_VISIBLE_MS)
  );

  return {
    key,
    selector,
    enterThreshold,
    exitThreshold,
    minVisibleMs,
    trackExit: raw.trackExit ?? DEFAULT_TRACK_EXIT,
    once: raw.once ?? DEFAULT_ONCE,
    includeElement: raw.includeElement ?? DEFAULT_INCLUDE_ELEMENT,
  };
}
